import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { WasteKind } from "generated-graphql/graphql";

export const prettyPrintWasteClassification = (
  classification: string | null | undefined
): string => {
  if (!classification) return "";
  return prettyPrintEnumValue(classification)?.replace("Rcra", "RCRA") ?? "";
};

/**
 * The management program on its own, blank when no special program applies.
 *
 * Blank rather than "RCRA" so a scan down the grid's Management Program column
 * surfaces only the streams on a special program. A null kind reads the same
 * way, whether it was never derived or the tenant's feature flag withholds it.
 *
 * This is also the single place the "RCRA or null means no program" rule
 * lives, so the combined label below cannot drift from the column.
 */
export const prettyPrintWasteProgram = (wasteKind: WasteKind | null | undefined): string =>
  !wasteKind || wasteKind === WasteKind.Rcra ? "" : prettyPrintEnumValue(wasteKind);

/**
 * A stream's hazard class and management program as one label, program first:
 * "Universal Waste · RCRA Hazardous".
 *
 * The two are separate fields because state programs pull them apart — a
 * federal universal waste category in a state that hasn't adopted it is
 * regulated hazardous waste there — but a reader wants one label, so they are
 * joined here and nowhere upstream.
 *
 * `WasteKind.Rcra` and a null kind both mean no special program applies, and
 * both return the classification by itself. That covers ordinary streams and
 * tenants whose feature flag withholds the kind, so those labels are
 * byte-identical to what they were before the kind existed.
 */
export const prettyPrintWasteStreamLabel = (
  classification: string | null | undefined,
  wasteKind: WasteKind | null | undefined
): string => {
  const hazard = prettyPrintWasteClassification(classification);
  const program = prettyPrintWasteProgram(wasteKind);
  if (!program) return hazard;

  return hazard ? `${program} · ${hazard}` : program;
};
